import type { CreditCard } from "@b2bportal/card-api";

/**
 * Card-payment state that keeps every card verified this session, not just
 * the last one.
 *
 * A card the customer adds without saving it is stored as single-use, and card
 * lists may leave single-use cards out. Every checkout that reloads the card
 * list after verify uses these helpers so every card verified this session
 * stays selectable, even after verifying several and switching between them.
 */
export interface VerifiedPaymentMethodState<T extends { id?: string }> {
  paymentMethods: T[];
  /** Cards verified this session, oldest first, de-duplicated by id. */
  verifiedPaymentMethods: T[];
}

/**
 * Returns `paymentMethods` with `verifiedPaymentMethod` added at the front
 * when it is not already in the list (matched by card id).
 */
export const mergeVerifiedPaymentMethod = <
  T extends { id?: string } = CreditCard,
>(
  paymentMethods: T[] | undefined,
  verifiedPaymentMethod: T | undefined
): T[] => {
  const list = paymentMethods ?? [];
  if (!verifiedPaymentMethod?.id) {
    return list;
  }
  return list.some((card) => card.id === verifiedPaymentMethod.id)
    ? list
    : [verifiedPaymentMethod, ...list];
};

/**
 * Returns `paymentMethods` with every card in `verifiedPaymentMethods` that
 * isn't already in the list (matched by card id) added at the front, most
 * recently verified first. The list's own order is otherwise unchanged.
 */
export const mergeVerifiedPaymentMethods = <
  T extends { id?: string } = CreditCard,
>(
  paymentMethods: T[] | undefined,
  verifiedPaymentMethods: T[] | undefined
): T[] =>
  // `verifiedPaymentMethods` is oldest-verified first. Folding oldest to
  // newest and prepending each in turn leaves the most recently verified
  // card nearest the front of the result.
  (verifiedPaymentMethods ?? []).reduce(
    (list, verified) => mergeVerifiedPaymentMethod(list, verified),
    paymentMethods ?? []
  );

/**
 * Returns the card a successful verify added: the card verify returned, or the
 * card in the reloaded list with the selected id when verify returned none.
 */
export const getVerifiedPaymentMethod = <
  T extends { id?: string } = CreditCard,
>(response: {
  paymentMethods?: T[];
  selectedPaymentMethodId?: string;
  verifiedPaymentMethod?: T;
}): T | undefined =>
  response.verifiedPaymentMethod ??
  response.paymentMethods?.find(
    (card) => card.id === response.selectedPaymentMethodId
  );

/**
 * Remembers the card a successful verify returned: appended to the list of
 * cards verified this session, or replacing the entry with the same id when
 * that card was verified before.
 */
export const rememberVerifiedPaymentMethod = <T extends { id?: string }>(
  state: VerifiedPaymentMethodState<T>,
  verifiedPaymentMethod: T | undefined
): void => {
  if (!verifiedPaymentMethod) {
    return;
  }
  const remembered = state.verifiedPaymentMethods ?? [];
  state.verifiedPaymentMethods = [
    ...remembered.filter((card) => card.id !== verifiedPaymentMethod.id),
    verifiedPaymentMethod,
  ];
};

/**
 * Sets the card list from a reload (after verify, on returning to the payment
 * step, after a delete), keeping every card verified this session in it, most
 * recently verified first.
 */
export const setPaymentMethodsKeepingVerified = <T extends { id?: string }>(
  state: VerifiedPaymentMethodState<T>,
  paymentMethods: T[] | undefined
): void => {
  state.paymentMethods = mergeVerifiedPaymentMethods(
    paymentMethods,
    state.verifiedPaymentMethods
  );
};

/**
 * Sets the card list reloaded after a delete. Deleting a remembered card
 * forgets just that one card; every other remembered card is still merged
 * back.
 */
export const setPaymentMethodsAfterDelete = <T extends { id?: string }>(
  state: VerifiedPaymentMethodState<T>,
  paymentMethods: T[] | undefined,
  deletedPaymentId: string | undefined
): void => {
  if (deletedPaymentId !== undefined) {
    state.verifiedPaymentMethods = (state.verifiedPaymentMethods ?? []).filter(
      (card) => card.id !== deletedPaymentId
    );
  }
  setPaymentMethodsKeepingVerified(state, paymentMethods);
};
